import Vue from 'vue';
import moment from 'moment';
import FeedbackChatPanel from '@/components/feedback/FeedbackChatPanel.vue';
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';

export default Vue.extend({
  name: 'FeedbackDetail',
  
  components: {
    FeedbackChatPanel
  },

  data() {
    return {
      loading: false,
      sending: false,
      updating: false,
      newMessage: '',
      selectedFiles: [] as File[],
      updateDialog: false,
      newStatus: '',
      newResolution: '',
      resolutionNote: '',
      allStatusOptions: [
        { text: 'New', value: 'New', resolution: 'Ongoing' },
        { text: 'Open - Ongoing', value: 'Open', resolution: 'Ongoing' },
        { text: 'Pending - Waiting on family', value: 'Pending', resolution: 'WaitingOnFamily' },
        { text: 'Closed - Resolved', value: 'Closed', resolution: 'Resolved' }
      ]
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.authModule.user;
    },

    canViewFamilyFeedback() {
      return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.FamilyFeedback_ViewDetails);
    },

    feedbackRequest() {
      return this.$store.state.feedbackModule.currentFeedbackRequest;
    },

    canSendMessage(): boolean {
      if (!this.feedbackRequest) return false;
      return this.feedbackRequest.status === 'New' || this.feedbackRequest.status === 'Open' || this.feedbackRequest.status === 'Pending';
    },

    canUpdate(): boolean {
      if (!this.feedbackRequest) return false;
      return this.feedbackRequest.status !== 'Closed';
    },

    statusOptions(): Array<{ text: string; value: string; resolution: string }> {
      // 'New' is an initial status set by the system and should not be selectable by staff
      return this.allStatusOptions.filter(opt => opt.value !== 'New');
    }
  },

  async mounted() {
    await this.loadFeedbackRequest();
  },

  methods: {
    async loadFeedbackRequest() {
      // Check if user has permission to access this page
      if (!this.canViewFamilyFeedback) {
        this.$toast.open({
          message: 'You do not have permission to access Family Feedback Requests',
          type: 'error'
        });
        this.$router.push('/dashboard');
        return;
      }
      
      this.loading = true;
      try {
        const requestId = this.$route.params.id;
        await this.$store.dispatch('getFeedbackRequestById', requestId);
        
        if (!this.feedbackRequest) {
          this.$toast.open({
            message: 'Feedback request not found',
            type: 'error'
          });
          this.$router.push('/family-feedback-requests');
          return;
        }
        
        this.newStatus = this.feedbackRequest.status;
      } catch (error) {
        console.error('Error loading feedback request:', error);
        
        let errorMessage = this.$t('GeneralTranslations.UnhandledError') as string;
        if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
          errorMessage = 'You do not have permission to view this feedback request';
        } else if (error?.message?.includes('not found')) {
          errorMessage = 'Feedback request not found';
        }
        
        this.$toast.open({
          message: errorMessage,
          type: 'error'
        });
        
        this.$router.push('/family-feedback-requests');
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.push('/family-feedback-requests');
    },

    getInitials(name: string): string {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(date: any): string {
      if (!date) return '';
      
      // Handle Firestore Timestamp
      if (date.toDate) {
        return moment(date.toDate()).format('HH:mm, DD MMM YYYY');
      }
      
      return moment(date).format('HH:mm, DD MMM YYYY');
    },

    formatDateDetail(date: any): string {
      if (!date) return '';
      
      // Handle Firestore Timestamp
      if (date.toDate) {
        return moment(date.toDate()).format('DD/MM/YYYY');
      }
      
      return moment(date).format('DD/MM/YYYY');
    },

    getStatusColor(status: string): string {
      const colors: Record<string, string> = {
        'New': 'info',
        'Open': 'success',
        'Pending': 'warning',
        'Closed': 'grey'
      };
      return colors[status] || 'grey';
    },

    getTypeColor(type: string): string {
      const colors: Record<string, string> = {
        'Feedback': 'info',
        'Question': 'primary'
      };
      return colors[type] || 'grey';
    },

    handleFileSelect(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files) {
        const validFiles = Array.from(target.files).filter(file => {
          const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
          if (!isImage) {
            this.$toast.open({
              message: `${file.name} is not a valid image type. Only JPEG and PNG are allowed.`,
              type: 'error'
            });
          }
          return isImage;
        });
        
        this.selectedFiles.push(...validFiles);
      }
      // Reset input
      target.value = '';
    },

    removeFile(index: number) {
      this.selectedFiles.splice(index, 1);
    },

    async sendMessage() {
      if (!this.newMessage.trim()) return;

      this.sending = true;
      try {
        const requestId = this.$route.params.id;
        await this.$store.dispatch('addCommentToFeedbackRequest', {
          requestId,
          text: this.newMessage.trim(),
          attachments: this.selectedFiles
        });

        this.newMessage = '';
        this.selectedFiles = [];

        this.$toast.open({
          message: 'Message sent successfully',
          type: 'success'
        });

        // Scroll to bottom of comments
        this.$nextTick(() => {
          const commentsList = this.$el.querySelector('.comments-list');
          if (commentsList) {
            commentsList.scrollTop = commentsList.scrollHeight;
          }
        });
      } catch (error) {
        console.error('Error sending message:', error);
        this.$toast.open({
          message: 'Failed to send message',
          type: 'error'
        });
      } finally {
        this.sending = false;
      }
    },

    async updateStatus() {
      if (!this.newStatus) return;

      this.updating = true;
      try {
        const requestId = this.$route.params.id;
        await this.$store.dispatch('updateFeedbackRequestStatus', {
          requestId,
          referenceId: this.feedbackRequest?.referenceId,
          status: this.newStatus,
          resolution: this.newResolution,
          resolutionNote: this.newStatus === 'Closed' ? this.resolutionNote : undefined
        });

        this.$toast.open({
          message: 'Feedback request updated successfully',
          type: 'success'
        });

        this.updateDialog = false;
        
        // Reload the request to get updated data
        await this.loadFeedbackRequest();
      } catch (error) {
        console.error('Error updating status:', error);
        this.$toast.open({
          message: 'Failed to update feedback request',
          type: 'error'
        });
      } finally {
        this.updating = false;
      }
    },

    onStatusChange() {
      const selectedOption = this.statusOptions.find(opt => opt.value === this.newStatus);
      if (selectedOption) {
        this.newResolution = selectedOption.resolution;
      }
    },

    viewAttachment(url: string) {
      window.open(url, '_blank');
    },

    // Type guards for careNote
    isCareNote(careNote: any): careNote is import('@/models/FeedbackModels').CareNote {
      return careNote && 'careNoteID' in careNote;
    },

    isCarePlanDailyTask(careNote: any): careNote is import('@/models/FeedbackModels').CarePlanDailyTask {
      return careNote && 'title' in careNote && 'periodName' in careNote;
    },

    isCareSummaryCategories(careNote: any): careNote is import('@/models/FeedbackModels').CareSummaryCategories {
      return careNote && 'name' in careNote && 'count' in careNote && 'percentage' in careNote;
    },

    navigateToResident() {
      if (this.feedbackRequest?.residentId) {
        this.$router.push(`/residents/${this.feedbackRequest.residentId}`);
      }
    },

    navigateToFamilyMember() {
      if (this.feedbackRequest?.familyMemberId) {
        this.$router.push(`/family-members/${this.feedbackRequest.familyMemberId}`);
      }
    }
  }
});
